import React, { useMemo, useState, useEffect } from "react";
import {
    Modal,
    Box,
    Typography,
    Button,
    Stack,
    IconButton,
    InputLabel,
    Input,
    Alert,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Checkbox,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Card,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    TableContainer,
    LinearProgress,
    Chip,
    TextField,
    InputAdornment,
    Switch,
    FormControlLabel,
    TablePagination,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import { useAuth } from "./TokenHandler";

/* ------------------------ Date helpers ------------------------ */
function formatLocalDate(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}
function getDefaultDates() {
    const today = new Date();
    const startObj = new Date(today);
    startObj.setDate(startObj.getDate() - 30);
    return {
        startDefault: formatLocalDate(startObj),
        stopDefault: formatLocalDate(today),
    };
}
function toIsoZDayStart(localDateStr) {
    if (!localDateStr) return "";
    const [y, m, d] = localDateStr.split("-").map(Number);
    const dt = new Date(y, m - 1, d, 0, 0, 0, 0);
    return dt.toISOString();
}
function toIsoZDayEnd(localDateStr) {
    if (!localDateStr) return "";
    const [y, m, d] = localDateStr.split("-").map(Number);
    const dt = new Date(y, m - 1, d, 23, 59, 59, 999);
    return dt.toISOString();
}

/* ------------------------ Misc helpers ------------------------ */
const fmtDate = (v) => {
    if (!v) return "";
    try {
        const d = new Date(v);
        if (isNaN(d.getTime())) return String(v);
        const yy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yy}-${mm}-${dd}`;
    } catch {
        return String(v);
    }
};

// max facility length
const FACILITY_NAME_MAX = 12;
const truncateFacilityName = (name) => {
    const s = String(name ?? "");
    return s.length > FACILITY_NAME_MAX ? s.slice(0, FACILITY_NAME_MAX - 1) + "…" : s;
};

// Helper to check if a log row is active
const isRowActive = (r) =>
    r?.status &&
    typeof r.status === "object" &&
    String(r.status.status || "").toLowerCase() === "active";

const isYes = (v) => String(v ?? "").trim().toLowerCase() === "yes";
const parsePgTs = (s) => {
    if (!s) return -Infinity;
    const d = new Date(String(s).replace(" ", "T"));
    return isNaN(d.getTime()) ? -Infinity : d.getTime();
};

// Pick latest "Tapestry Opt Out Application" record
function pickLatestTapestry(records = []) {
    const tapestry = records.filter(
        (r) => String(r?.source_system).trim() === "Tapestry Opt Out Application"
    );
    if (tapestry.length === 0) return null;
    return tapestry.reduce((latest, r) =>
        parsePgTs(r?.modifiedon) > parsePgTs(latest?.modifiedon) ? r : latest
    );
}
// Roll up calculated fields across ALL records ("Yes" wins)
function rollupCalculated(records = []) {
    const ccmYes = records.some((r) => isYes(r?.calculatedccm));
    const rpmYes = records.some((r) => isYes(r?.calculatedrpm));
    const bhiYes = records.some((r) => isYes(r?.calculatedbhi));
    return {
        ccm: ccmYes ? "Yes" : "No",
        rpm: rpmYes ? "Yes" : "No",
        bhi: bhiYes ? "Yes" : "No",
    };
}
// Compute current_status for a single patient's history array
function computeCurrentStatusForPatient(history = []) {
    const latestTapestry = pickLatestTapestry(history);
    if (latestTapestry) {
        return {
            ccm: isYes(latestTapestry?.ccm) ? "Yes" : "No",
            rpm: isYes(latestTapestry?.rpm) ? "Yes" : "No",
            bhi: isYes(latestTapestry?.bhi) ? "Yes" : "No",
        };
    }
    return rollupCalculated(history);
}

/* Compact chips for "Current Status" (CCM/RPM/BHI) */
const renderCurrentStatusChips = (status) => {
    if (!status) return null;
    const order = ["ccm", "rpm", "bhi"];
    return (
        <Stack direction="row" spacing={1} alignItems="center">
            {order.map((k) => {
                const yes = isYes(status?.[k]);
                return (
                    <Chip
                        key={k}
                        label={k.toUpperCase()}
                        size="small"
                        sx={{
                            bgcolor: yes ? "error.main" : "success.main",
                            color: "white",
                            fontWeight: "bold",
                        }}
                    />
                );
            })}
        </Stack>
    );
};

const SELECT_COL_WIDTH = 52; // keep in sync with the first TableCell width

/* Short chips for a status object {ccm,rpm,bhi} */
const renderShortStatusChips = (obj) => {
    if (!obj || typeof obj !== "object") return null;
    const keys = ["ccm", "rpm", "bhi"];
    return (
        <Stack direction="row" spacing={1} flexWrap="wrap">
            {keys.map((key) => {
                if (!(key in obj)) return null;
                const yes = isYes(obj[key]);
                return (
                    <Chip
                        key={key}
                        label={key.toUpperCase()}
                        size="small"
                        sx={{
                            bgcolor: yes ? "error.main" : "success.main",
                            color: "white",
                            fontWeight: "bold",
                        }}
                    />
                );
            })}
        </Stack>
    );
};

/* ------------------------ Component ------------------------ */
export const LogModal = ({ open, onClose }) => {
    const modalStyle = {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 1600,
        maxHeight: "80vh",
        bgcolor: "background.paper",
        borderRadius: "8px",
        boxShadow: 24,
        p: 4,
        display: "flex",
        flexDirection: "column",
    };

    const { keycloak: kc } = useAuth();

    // date inputs
    const [startDate, setStartDate] = useState(() => getDefaultDates().startDefault);
    const [stopDate, setStopDate] = useState(() => getDefaultDates().stopDefault);

    // server-side search (q)
    const [search, setSearch] = useState("");

    // pagination state (API is 1-based page indexing)
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);

    // data + ui state
    const [isLoading, setIsLoading] = useState(false);
    const [apiError, setApiError] = useState("");
    const [apiSuccess, setApiSuccess] = useState("");
    const [logs, setLogs] = useState([]); // current page results only

    // group toggle: 'patient' | 'batch'
    const [groupMode, setGroupMode] = useState("patient");

    // selection for Undo
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [rollbackLoading, setRollbackLoading] = useState(false);

    // Select or unselect all active rows in a batch
    const selectBatch = (rows, select) => {
        const ids = rows.filter(isRowActive).map((r) => r.id);
        setSelectedIds((prev) => {
            const next = new Set(prev);
            ids.forEach((id) => (select ? next.add(id) : next.delete(id)));
            return next;
        });
    };

    // current DnD log per patient_id { [pid]: { current_status, history } }
    const [current_dnd_log, setCurrent_dnd_log] = useState({});

    const resetSelection = () => setSelectedIds(new Set());

    const toggleSelect = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleUndoClick = () => {
        setApiSuccess("");
        setApiError("");
        setConfirmOpen(true);
    };

    const doRollback = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) {
            setConfirmOpen(false);
            return;
        }
        try {
            setRollbackLoading(true);
            setApiError("");
            await axios.post(
                `${process.env.REACT_APP_API_URL}/rollback_log`,
                { ids },
                {
                    headers: {
                        "Content-Type": "application/json",
                        ...(kc?.token ? { Authorization: `Bearer ${kc.token}` } : {}),
                    },
                }
            );
            setConfirmOpen(false);
            setApiSuccess("Undo completed successfully.");
            resetSelection();
            await handleFetch(); // refresh current page
        } catch (err) {
            console.error("rollback_log error:", err);
            setApiError(
                err?.response?.data?.error ||
                err?.message ||
                "Failed to undo. Please try again."
            );
        } finally {
            setRollbackLoading(false);
        }
    };

    /* ------------------------ NEW: ID collectors ------------------------ */
    // safely coerce various shapes into an array of strings
    const toArray = (v) => Array.isArray(v) ? v : (v === null || v === undefined ? [] : [v]);
    // normalize UUID-ish values (leave as-is; backend normalizes loosely)
    const normUuid = (x) => String(x ?? "").trim();
    // normalize numeric IDs as strings (backend casts to bigint)
    const normPuid = (x) => String(x ?? "").trim();

    // Collect per-patient BOTH uuid[] and patient_unique_id[] from row shapes.
    // Supports multiple possible field names to be robust to API variations.
    // Collect per-patient BOTH ids, preferring exact row pairs from `id_pairs`
    const buildPatientIdentityMap = (rows) => {
        const toArray = (v) => Array.isArray(v) ? v : (v == null ? [] : [v]);
        const normUuid = (x) => String(x ?? "").trim();
        const normPuid = (x) => String(x ?? "").trim();

        const map = new Map(); // patient_id -> { uuids:Set, puids:Set }

        for (const r of rows) {
            const pidKey = r?.patient_id != null ? String(r.patient_id) : null;
            if (!pidKey) continue;

            if (!map.has(pidKey)) map.set(pidKey, { uuids: new Set(), puids: new Set() });
            const entry = map.get(pidKey);

            // 1) Prefer exact pairs from API
            toArray(r?.id_pairs).forEach(p => {
                if (!p || typeof p !== "object") return;
                if (p.patient_uuid) entry.uuids.add(normUuid(p.patient_uuid));
                if (p.patient_unique_id !== undefined && p.patient_unique_id !== null)
                    entry.puids.add(normPuid(p.patient_unique_id));
            });

            // 2) Back-compat fallbacks (arrays or singletons)
            toArray(r?.uuids).forEach(u => u && entry.uuids.add(normUuid(u)));
            toArray(r?.patient_unique_ids).forEach(v => v != null && entry.puids.add(normPuid(v)));

            if (r?.patient_uuid) entry.uuids.add(normUuid(r.patient_uuid));
            if (r?.patient_unique_id != null) entry.puids.add(normPuid(r.patient_unique_id));
        }

        const out = {};
        for (const [pid, { uuids, puids }] of map.entries()) {
            out[pid] = {
                patient_uuid: Array.from(uuids),
                patient_unique_id: Array.from(puids),
            };
        }
        return out;
    };


    // fetch per-patient history sending BOTH uuid[] and patient_unique_id[]
    const fetchCurrentDndHistory = async (rows) => {
        const patientIdentities = buildPatientIdentityMap(rows);
        const entries = Object.entries(patientIdentities);
        if (entries.length === 0) {
            setCurrent_dnd_log({});
            console.log("current_dnd_log", {});
            return;
        }

        try {
            const headers = {
                "Content-Type": "application/json",
                ...(kc?.token ? { Authorization: `Bearer ${kc.token}` } : {}),
            };

            const results = await Promise.allSettled(
                entries.map(([pid, ids]) =>
                    axios
                        .post(
                            `${process.env.REACT_APP_API_URL}/get_dnd_history_uuid`,
                            // send both; backend accepts empty arrays too
                            {
                                patient_uuid: ids.patient_uuid || [],
                                patient_unique_id: ids.patient_unique_id || [],
                            },
                            { headers }
                        )
                        .then((resp) => ({
                            pid,
                            data: Array.isArray(resp?.data) ? resp.data : [],
                        }))
                )
            );

            const map = {};
            for (const r of results) {
                if (r.status === "fulfilled") {
                    const pid = r.value.pid;
                    const history = r.value.data;
                    const current_status = computeCurrentStatusForPatient(history);
                    map[pid] = { current_status, history };
                } else {
                    // fallback label (we still key by patient_id)
                    map["unknown"] = { current_status: { ccm: "No", rpm: "No", bhi: "No" }, history: [] };
                }
            }

            setCurrent_dnd_log(map);
            console.log("current_dnd_log", map);
        } catch (err) {
            console.error("get_dnd_history_uuid (batch) error:", err);
            setCurrent_dnd_log({});
            console.log("current_dnd_log", {});
        }
    };

    // Core fetch: supports overrides for page/pageSize/q to avoid race conditions when changing them
    const handleFetch = async (overrides = {}) => {
        setApiError("");
        setApiSuccess("");
        setIsLoading(true);
        resetSelection();

        const pageToUse = overrides.page ?? page;
        const pageSizeToUse = overrides.pageSize ?? pageSize;
        const qToUse = overrides.q ?? search;

        try {
            const payload = {
                start: toIsoZDayStart(startDate),
                stop: toIsoZDayEnd(stopDate),
                q: qToUse ?? "",
                page: pageToUse,
                pageSize: pageSizeToUse,
            };

            const resp = await axios.post(
                `${process.env.REACT_APP_API_URL}/view_dnd_logs_v2`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        ...(kc?.token ? { Authorization: `Bearer ${kc.token}` } : {}),
                    },
                }
            );

            const {
                page: pg = 1,
                pageSize: ps = pageSizeToUse,
                total: ttl = 0,
                totalPages: tpg = 0,
                hasNextPage: hnp = false,
                hasPrevPage: hpp = false,
                results = [],
            } = resp?.data || {};

            setPage(pg);
            setPageSize(ps);
            setTotal(ttl);
            setTotalPages(tpg);
            setHasNextPage(hnp);
            setHasPrevPage(hpp);

            const data = Array.isArray(results) ? results : [];
            setLogs(data);
            fetchCurrentDndHistory(data); // kick off per-patient status fetch (now sends UUIDs + PUIDs)
        } catch (err) {
            console.error("view_dnd_logs_v2 error:", err);
            setApiError(
                err?.response?.data?.error ||
                err?.message ||
                "Failed to fetch logs. Please try again."
            );
            setLogs([]);
            setCurrent_dnd_log({});
            setTotal(0);
            setTotalPages(0);
            setHasNextPage(false);
            setHasPrevPage(false);
        } finally {
            setIsLoading(false);
        }
    };

    /* ---------- server-driven results (no local filter) ---------- */
    const filteredRows = logs; // server handles q

    /* ---------- group by patient & group by batch ---------- */
    const groupsByPatient = useMemo(() => {
        const map = new Map();
        for (const r of filteredRows) {
            const key = r?.patient_id ?? "Unknown";
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(r);
        }
        return map;
    }, [filteredRows]);

    const groupsByBatch = useMemo(() => {
        const map = new Map();
        for (const r of filteredRows) {
            const key = r?.batch_id ?? "Unknown";
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(r);
        }
        return map;
    }, [filteredRows]);

    // choose which groups to render + simple readable ordering
    const orderedGroups = useMemo(() => {
        if (groupMode === "batch") {
            const entries = [...groupsByBatch.entries()];
            return entries.sort((a, b) => (a[0] ?? "").localeCompare(b[0] ?? ""));
        }
        const entries = [...groupsByPatient.entries()];
        return entries.sort((a, b) => {
            const ax = a[1]?.[0] ?? {};
            const bx = b[1]?.[0] ?? {};
            const an = `${ax.last_name ?? ""}, ${ax.first_name ?? ""}`.toLowerCase();
            const bn = `${bx.last_name ?? ""}, ${bx.first_name ?? ""}`.toLowerCase();
            return an.localeCompare(bn);
        });
    }, [groupMode, groupsByBatch, groupsByPatient]);

    const handleClose = () => {
        setIsLoading(false);
        setApiError("");
        setApiSuccess("");
        onClose();
    };

    // Pagination handlers (TablePagination is 0-based; API is 1-based)
    const handleChangePage = (_evt, newPageZero) => {
        const newPage = newPageZero + 1;
        setPage(newPage);
        handleFetch({ page: newPage });
    };
    const handleChangeRowsPerPage = (evt) => {
        const newSize = parseInt(evt.target.value, 10);
        setPageSize(newSize);
        setPage(1);
        handleFetch({ page: 1, pageSize: newSize });
    };

    useEffect(() => {
        if (open) {
            const { startDefault, stopDefault } = getDefaultDates();
            setStartDate(startDefault);
            setStopDate(stopDefault);
            setSearch("");
            setPage(1);
            setPageSize(50);
            resetSelection();
            setLogs([]);
            setApiError("");
            setApiSuccess("");
            setCurrent_dnd_log({});
            setTotal(0);
            setTotalPages(0);
            setHasNextPage(false);
            setHasPrevPage(false);
        }
    }, [open]);

    // Optional: fetch immediately when the modal opens
    useEffect(() => {
        if (open) {
            handleFetch({ page: 1 });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // derived display text
    const fromIdx = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const toIdx = Math.min(page * pageSize, total);

    return (
        <Modal open={open} onClose={handleClose} aria-labelledby="log-modal-title">
            <Box sx={modalStyle}>
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography id="log-modal-title" variant="h6">History</Typography>
                    <IconButton onClick={handleClose} title="Close Modal">
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Controls row */}
                <Stack direction="row" spacing={2} alignItems="flex-end" mb={1} sx={{ width: "100%" }}>
                    {/* Start / Stop */}
                    <Box>
                        <InputLabel htmlFor="start-dt">Start (local)</InputLabel>
                        <Input
                            id="start-dt"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </Box>

                    <Box>
                        <InputLabel htmlFor="stop-dt">Stop (local)</InputLabel>
                        <Input
                            id="stop-dt"
                            type="date"
                            value={stopDate}
                            onChange={(e) => setStopDate(e.target.value)}
                        />
                    </Box>

                    {/* Clear */}
                    <Button
                        variant="text"
                        onClick={() => {
                            setLogs([]);
                            resetSelection();
                            setApiError("");
                            setApiSuccess("");
                            setCurrent_dnd_log({});
                            setSearch("");
                            setPage(1);
                            setPageSize(50);
                            setTotal(0);
                            setTotalPages(0);
                            setHasNextPage(false);
                            setHasPrevPage(false);
                        }}
                        disabled={isLoading || (logs.length === 0 && !total)}
                        sx={{ alignSelf: "flex-end" }}
                    >
                        Clear
                    </Button>

                    {/* Fetch */}
                    <Button
                        variant="contained"
                        onClick={() => {
                            setPage(1);
                            handleFetch({ page: 1 });
                        }}
                        disabled={!startDate || !stopDate || isLoading}
                    >
                        Fetch Logs
                    </Button>

                    {/* Spacer between Fetch and Search */}
                    <Box sx={{ width: 200, flexShrink: 0 }} />

                    {/* Server Search (q) */}
                    <TextField
                        label="Search (q: MRN, name, facility, etc.)"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                setPage(1);
                                handleFetch({ page: 1, q: e.currentTarget.value });
                            }
                        }}
                        size="small"
                        sx={{ minWidth: 360 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            endAdornment: search ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setSearch("");
                                            setPage(1);
                                            handleFetch({ page: 1, q: "" });
                                        }}
                                        aria-label="Clear search"
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />

                    {/* Group toggle */}
                    <FormControlLabel
                        sx={{ ml: 2, alignSelf: "flex-end" }}
                        control={
                            <Switch
                                checked={groupMode === "batch"}
                                onChange={(e) => setGroupMode(e.target.checked ? "batch" : "patient")}
                                color="primary"
                            />
                        }
                        label={groupMode === "batch" ? "Group: Batch ID" : "Group: Patient"}
                    />

                    {/* push Undo to far right */}
                    <Box sx={{ flexGrow: 1 }} />

                    <Button
                        variant="outlined"
                        onClick={handleUndoClick}
                        disabled={isLoading || rollbackLoading || selectedIds.size === 0}
                    >
                        Undo
                    </Button>
                </Stack>

                {/* Loading bar */}
                {isLoading && <LinearProgress sx={{ mb: 1 }} />}

                {/* Summary line */}
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="caption" color="text.secondary">
                        Showing {fromIdx.toLocaleString()}–{toIdx.toLocaleString()} of {total.toLocaleString()} • Page {page} / {Math.max(totalPages, 1)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {hasPrevPage ? "Has Previous" : " "} {hasNextPage ? "• Has Next" : ""}
                    </Typography>
                </Box>

                {/* Alerts */}
                {apiError && <Alert severity="error" sx={{ mb: 2 }}>{apiError}</Alert>}
                {apiSuccess && <Alert severity="success" sx={{ mb: 2 }}>{apiSuccess}</Alert>}

                {/* Scrollable results */}
                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                    {orderedGroups.map(([groupKey, rows], idx) => {
                        const keyId = String(groupKey);
                        const exemplar = rows[0] || {};
                        const patientStatus =
                            groupMode === "patient" ? current_dnd_log[keyId]?.current_status : undefined;

                        return (
                            <Card key={`${groupMode}-${keyId}-${idx}`} elevation={0} sx={{ mb: 2, border: 1, borderColor: "divider" }}>
                                <Accordion defaultExpanded disableGutters elevation={0} sx={{ "&:before": { display: "none" } }}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{ "& .MuiAccordionSummary-content": { width: "100%", alignItems: "center" } }}
                                    >
                                        <Grid container alignItems="center" spacing={2} wrap="nowrap" sx={{ width: "100%" }}>
                                            {groupMode === "patient" ? (
                                                <>
                                                    <Grid item>
                                                        <Typography variant="subtitle1" fontWeight={700}>
                                                            Patient ID: {keyId}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {exemplar?.first_name ?? ""} {exemplar?.last_name ?? ""} • MRN {exemplar?.mrn ?? ""} • {fmtDate(exemplar?.dob)}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {rows.length} entr{rows.length === 1 ? "y" : "ies"}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs />
                                                    <Grid item>
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <Typography variant="overline" color="text.secondary">Current Status</Typography>
                                                            {renderCurrentStatusChips(patientStatus)}
                                                        </Stack>
                                                    </Grid>
                                                </>
                                            ) : (
                                                <>
                                                    <Grid
                                                        item
                                                        sx={{ width: SELECT_COL_WIDTH, pl: 2, display: "flex", alignItems: "center" }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onKeyDown={(e) => e.stopPropagation()}
                                                    >
                                                        {(() => {
                                                            const activeIds = rows.filter(isRowActive).map((r) => r.id);
                                                            const selectedCount = activeIds.filter((id) => selectedIds.has(id)).length;
                                                            const allSelected = activeIds.length > 0 && selectedCount === activeIds.length;
                                                            const someSelected = selectedCount > 0 && !allSelected;

                                                            return (
                                                                <Tooltip title={`Select all VISIBLE active rows in this group (${activeIds.length})`}>
                                                                    <span>
                                                                        <Checkbox
                                                                            size="small"
                                                                            checked={allSelected}
                                                                            indeterminate={someSelected}
                                                                            disabled={activeIds.length === 0}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            onChange={(e) => {
                                                                                e.stopPropagation();
                                                                                selectBatch(rows, !allSelected);
                                                                            }}
                                                                            inputProps={{
                                                                                "aria-label": "Select all visible active rows in this batch group (this page/section)",
                                                                            }}
                                                                        />
                                                                    </span>
                                                                </Tooltip>
                                                            );
                                                        })()}
                                                    </Grid>

                                                    <Grid item>
                                                        <Typography variant="subtitle1" fontWeight={700}>
                                                            Batch ID: {keyId}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {rows.length} entr{rows.length === 1 ? "y" : "ies"}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs />
                                                </>
                                            )}
                                        </Grid>
                                    </AccordionSummary>

                                    <AccordionDetails>
                                        <TableContainer>
                                            <Table size="small" sx={{ "td, th": { border: 0 } }}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell width={SELECT_COL_WIDTH} />

                                                        <TableCell>First Name</TableCell>
                                                        <TableCell>Last Name</TableCell>
                                                        <TableCell>MRN</TableCell>
                                                        <TableCell>DOB</TableCell>
                                                        <TableCell>Facility</TableCell>
                                                        <TableCell>Old</TableCell>
                                                        <TableCell>New</TableCell>
                                                        <TableCell>Method</TableCell>
                                                        <TableCell align="right">Change By</TableCell>
                                                        <TableCell align="right">Change Date</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {rows.map((r) => {
                                                        const isActive = isRowActive(r);

                                                        return (
                                                            <TableRow
                                                                key={r.id}
                                                                hover
                                                                sx={{
                                                                    bgcolor: isActive ? "inherit" : "grey.100",
                                                                    "& td, & th": {
                                                                        textDecoration: isActive ? "none" : "line-through",
                                                                        color: isActive ? "inherit" : "text.disabled",
                                                                    },
                                                                    "& .MuiChip-root": { opacity: isActive ? 1 : 0.6 },
                                                                    "& .MuiChip-label": {
                                                                        textDecoration: isActive ? "none" : "line-through",
                                                                    },
                                                                }}
                                                            >
                                                                <TableCell>
                                                                    {isActive ? (
                                                                        <Tooltip title="Mark for undo">
                                                                            <Checkbox
                                                                                size="small"
                                                                                checked={selectedIds.has(r.id)}
                                                                                onChange={() => toggleSelect(r.id)}
                                                                                inputProps={{ "aria-label": `select change ${r.id}` }}
                                                                            />
                                                                        </Tooltip>
                                                                    ) : null}
                                                                </TableCell>
                                                                <TableCell>{r.first_name ?? ""}</TableCell>
                                                                <TableCell>{r.last_name ?? ""}</TableCell>
                                                                <TableCell>{r.mrn ?? ""}</TableCell>
                                                                <TableCell>{fmtDate(r.dob)}</TableCell>

                                                                <TableCell>
                                                                    <Tooltip
                                                                        title={r.facility_name ?? ""}
                                                                        disableHoverListener={(r.facility_name ?? "").length <= FACILITY_NAME_MAX}
                                                                    >
                                                                        <span>{truncateFacilityName(r.facility_name)}</span>
                                                                    </Tooltip>
                                                                </TableCell>

                                                                <TableCell>{renderShortStatusChips(r.old_status)}</TableCell>
                                                                <TableCell>{renderShortStatusChips(r.new_status)}</TableCell>
                                                                <TableCell>{r.method ?? ""}</TableCell>
                                                                <TableCell align="right">{r.change_by ?? ""}</TableCell>
                                                                <TableCell align="right">{fmtDate(r.change_date)}</TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </AccordionDetails>
                                </Accordion>
                            </Card>
                        );
                    })}

                    {!isLoading && orderedGroups.length === 0 && (
                        <Box
                            sx={{
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 1,
                                p: 3,
                                textAlign: "center",
                                color: "text.secondary",
                            }}
                        >
                            <Typography variant="subtitle2" gutterBottom>No results</Typography>
                            <Typography variant="body2">
                                Try adjusting the date range, then click <strong>Fetch Logs</strong>.
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Pagination controls */}
                <Box mt={1}>
                    <TablePagination
                        component="div"
                        count={total}
                        page={Math.max(0, page - 1)}          // convert to 0-based
                        onPageChange={handleChangePage}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[25, 50, 100, 250]}
                        labelRowsPerPage="Rows per page"
                    />
                </Box>

                {/* Footer */}
                <Stack direction="row" spacing={2} justifyContent="flex-end" mt={2}>
                    <Button variant="outlined" onClick={handleClose}>Close</Button>
                </Stack>

                {/* Confirm Undo Dialog */}
                <Dialog open={confirmOpen} onClose={() => !rollbackLoading && setConfirmOpen(false)}>
                    <DialogTitle>Confirm Undo</DialogTitle>
                    <DialogContent>
                        <Typography>Are you sure you want to undo these opt out statuses?</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {Array.from(selectedIds).length} selected
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmOpen(false)} disabled={rollbackLoading}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={doRollback} disabled={rollbackLoading}>
                            {rollbackLoading ? "Undoing…" : "Yes, Undo"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Modal>
    );
};

export default LogModal;
